import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { getLocalStorageItem } from "../../utils/session";
import BulkCIP from "./BulkCIP";
import RecurrentCIP from "./RecurrentCIP";
import { CIP_SOURCE_OPTIONS } from "../../constants/global.constants";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { swapIcon } from "../../assets";
import "./CIPSpecification.scss";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import { CustomModalButtons } from "../../constants/global.constants";

const CIPSpecification = ({
  selectedTrain,
  selectedSheet,
  selectedCIPFileName,
  hasRecurrentCIPs,
  shouldScrollToRecurrent = false,
  mappedReference,
}) => {
  const [CIPMode, setCIPMode] = useState(null);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [CIPNeverShowAgain, setCIPNeverShowAgain] = useState(
    false || getLocalStorageItem("CIPneverShowAgain") === "true",
  );
  const [switchTo, setSwitchTo] = useState(null);
  const recurrentCIPRef = useRef(null);

  const { t } = useTranslation();

  const onSwitchConfirmClose = (action) => {
    setShowSwitchConfirm(false);
    if (action === CustomModalButtons.CONFIRM) {
      if (CIPMode === CIP_SOURCE_OPTIONS.BULK) {
        setSwitchTo(CIP_SOURCE_OPTIONS.RECURRENT);
      } else {
        setSwitchTo(CIP_SOURCE_OPTIONS.BULK);
      }
      setCIPMode(null);
    }
  };

  const switchLabel = `${t("CIPSwitch.switchTo")} ${
    CIPMode === CIP_SOURCE_OPTIONS.BULK
      ? t("CIPSwitch.recurrent")
      : t("CIPSwitch.upload")
  }`;

  // set initial CIPMode based on existing data
  useEffect(() => {
    if (hasRecurrentCIPs) {
      setCIPMode(CIP_SOURCE_OPTIONS.RECURRENT);
    } else if (selectedCIPFileName) {
      setCIPMode(CIP_SOURCE_OPTIONS.BULK);
    } else {
      setCIPMode(null);
    }
  }, [selectedTrain, selectedSheet, hasRecurrentCIPs, selectedCIPFileName]);

  // Scroll to RecurrentCIP when shouldScrollToRecurrent is true
  useEffect(() => {
    if (
      shouldScrollToRecurrent &&
      hasRecurrentCIPs &&
      recurrentCIPRef.current &&
      CIPMode === CIP_SOURCE_OPTIONS.RECURRENT
    ) {
      const scrollTimeout = setTimeout(() => {
        recurrentCIPRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 1500); // Delay to ensure content is fully loaded

      return () => clearTimeout(scrollTimeout);
    }
  }, [shouldScrollToRecurrent, hasRecurrentCIPs, CIPMode]);

  return (
    <div className={CIPMode ? "ro-card-wrapper" : ""}>
      <div
        className={`teal-heading ${CIPMode ? "ro-card-header" : "marginTop20 marginBottom12"}`}
      >
        {t("CIPSpecifications.yourCIPData")}
        {CIPMode !== null && (
          <>
            <WPTertiaryButton
              variant="outlined"
              label={switchLabel}
              onClick={() => setShowSwitchConfirm(true)}
              icon={<img src={swapIcon} alt="Swap Icon" />}
              iconPlacement="before"
              size="small"
            />
            {showSwitchConfirm && (
              <ConfirmationPopup
                header={
                  CIPMode === CIP_SOURCE_OPTIONS.BULK
                    ? t("CIPSwitch.bulkHeader")
                    : t("CIPSwitch.recurrentHeader")
                }
                type={DIALOG_TYPES.ERROR}
                description={
                  CIPMode === CIP_SOURCE_OPTIONS.BULK
                    ? t("CIPSwitch.bulkContent")
                    : t("CIPSwitch.recurrentContent")
                }
                confirmBtn={
                  CIPMode === CIP_SOURCE_OPTIONS.BULK
                    ? t("CIPSwitch.addRecurrent")
                    : t("CIPSwitch.uploadSheet")
                }
                cancelBtn={t("ValueStrings.cancel")}
                onClose={onSwitchConfirmClose}
              />
            )}
          </>
        )}
      </div>

      <div className={CIPMode === null ? "card-container" : ""}>
        {(CIPMode === CIP_SOURCE_OPTIONS.BULK || CIPMode === null) && (
          <BulkCIP
            setCIPMode={setCIPMode}
            CIPNeverShowAgain={CIPNeverShowAgain}
            setCIPNeverShowAgain={setCIPNeverShowAgain}
            selectedTrain={selectedTrain}
            selectedSheet={selectedSheet}
            existingCIPFileName={
              CIPMode === CIP_SOURCE_OPTIONS.BULK ? selectedCIPFileName : null
            }
            switchTo={switchTo}
            setSwitchTo={setSwitchTo}
            disabled={Boolean(!mappedReference)}
          />
        )}
        {(CIPMode === CIP_SOURCE_OPTIONS.RECURRENT || CIPMode === null) && (
          <div ref={recurrentCIPRef}>
            <RecurrentCIP
              setCIPMode={setCIPMode}
              CIPNeverShowAgain={CIPNeverShowAgain}
              setCIPNeverShowAgain={setCIPNeverShowAgain}
              selectedTrain={selectedTrain}
              selectedSheet={selectedSheet}
              hasRecurrentCIPs={
                CIPMode === CIP_SOURCE_OPTIONS.RECURRENT
                  ? hasRecurrentCIPs
                  : false
              }
              switchTo={switchTo}
              setSwitchTo={setSwitchTo}
              disabled={Boolean(!mappedReference)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CIPSpecification;
